import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { advisorApi } from '@/features/workspace/api';
import { DoctoralWorkflowForm } from '@/features/doctoral/components/DoctoralWorkflowView';
import { workflowTitles, type WorkflowModule } from '@/features/doctoral/workflowApi';
import { useAdvisorText as useStudyText } from '../labels';
import { useI18n } from '@/hooks/useI18n';
import { routes } from '@/lib/routes';
import { AdvisorResources } from './AdvisorResources';
import '@/styles/advisor-monitor.css';
import '@/features/doctoral/dp120.css';

export interface AdvisorCandidate {
  id: string;
  student_user_id?: string | null;
  academia_id: string;
  full_name: string;
  program: string;
  year: string;
  group_name?: string | null;
  supervisor?: string | null;
  topic?: string | null;
  review_state: string;
  plan_status: string;
  progress?: number | null;
  incomplete_sections: number;
  deadlines: { id: string; title: string; date: string }[];
}
const tabs = ['overview', 'year_1', 'year_2', 'year_3', 'templates'] as const;
const tabNames = ['Обзор', '1 курс', '2 курс', '3 курс', 'Образцы'];
export function AdvisorDoctoralProcessPage({ onClose }: { onClose?: () => void }) {
  const navigate = useNavigate();
  const st = useStudyText();
  const { locale } = useI18n();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'overview',
    search = params.get('search') || '';
  const program = params.get('program') || '',
    group = params.get('group') || '';
  const selected = params.get('candidate_id');
  const section = (params.get('section') || 'profile') as WorkflowModule;
  const [items, setItems] = useState<AdvisorCandidate[]>([]);
  const [candidate, setCandidate] = useState<AdvisorCandidate>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const sequence = useRef(0);
  const [refresh, setRefresh] = useState(0);
  const update = useCallback(
    (values: Record<string, string | null>) =>
      setParams((current) => {
        const next = new URLSearchParams(current);
        Object.entries(values).forEach(([key, value]) =>
          value ? next.set(key, value) : next.delete(key),
        );
        return next;
      }),
    [setParams],
  );
  useEffect(() => {
    document.body.classList.add('advisor-monitor-lock');
    return () => document.body.classList.remove('advisor-monitor-lock');
  }, []);
  useEffect(() => {
    if (tab === 'templates' && !selected) return;
    let cancelled = false;
    const request = ++sequence.current;
    setLoading(true);
    setError('');
    setCandidate(undefined);
    void advisorApi
      .module('doctoral_process', {
        tab,
        search: search || undefined,
        candidate_id: selected || undefined,
      })
      .then((payload) => {
        if (cancelled || sequence.current !== request) return;
        if (selected) {
          setCandidate((payload.dossier as { candidate: AdvisorCandidate })?.candidate);
        } else {
          setItems((payload.items as AdvisorCandidate[]) || []);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : st('Не удалось открыть досье'));
      })
      .finally(() => {
        if (!cancelled && sequence.current === request) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, search, selected, refresh, st]);
  const visible = items.filter(
    (c) => (!program || c.program === program) && (!group || c.group_name === group),
  );
  const active = visible.filter((c) => c.student_user_id);
  const summary: Record<string, number> = {
    total: active.length,
    needs_review: active.filter((c) => c.review_state === 'NEEDS_REVIEW').length,
    incomplete: active.filter((c) => c.incomplete_sections > 0).length,
    deadlines: active.reduce((count, c) => count + (c.deadlines?.length || 0), 0),
  };
  const options = (key: 'program' | 'group_name') =>
    Array.from(new Set(items.map((c) => c[key]).filter(Boolean))).sort() as string[];
  const year = (value: string) =>
    value.startsWith('YEAR_') ? `${value.slice(5)} ${st('курс')}` : st('Не указан');
  return (
    <div className="advisor-monitor-overlay advisor-monitor-root" role="dialog" aria-modal="true">
      <div className="advisor-monitor-shell">
        <header className="advisor-monitor-top">
          <button
            className="advisor-monitor-back"
            aria-label={st('Назад')}
            onClick={onClose || (() => navigate(routes.advisor))}
          >
            ←
          </button>
          <div>
            <small>Эдвайзер</small>
            <h1>{st('Учебный процесс докторантов')}</h1>
            <p>{st('Закреплённые докторанты')}</p>
          </div>
          <button
            className="advisor-monitor-close"
            aria-label={st('Закрыть')}
            onClick={onClose || (() => navigate(routes.advisor))}
          >
            ×
          </button>
        </header>
        <main className="advisor-monitor-main">
          {error && <p role="alert">{error}</p>}
          {loading ? (
            <p role="status">{st('Загрузка…')}</p>
          ) : selected ? (
            candidate && (
              <section className="advisor-monitor-panel">
                <header>
                  <div>
                    <h3>{candidate.full_name || candidate.academia_id}</h3>
                    <p>
                      {candidate.academia_id} · {year(candidate.year)} · {candidate.program || '—'}{' '}
                      · {candidate.group_name || '—'}
                    </p>
                  </div>
                  <button
                    className="advisor-monitor-mini"
                    onClick={() => update({ candidate_id: null, section: null })}
                  >
                    {st('К реестру')}
                  </button>
                </header>
                <div className="advisor-monitor-body">
                  <p>
                    {st('Консультант')}: {candidate.supervisor || '—'}
                  </p>
                  <p>
                    {st('Тема')}: {candidate.topic || '—'}
                  </p>
                  {candidate.student_user_id ? (
                    <>
                      <div className="advisor-monitor-filters">
                        <button
                          className="advisor-monitor-mini"
                          onClick={() =>
                            navigate(
                              `${routes.advisor}/messages?student_id=${candidate.student_user_id}`,
                            )
                          }
                        >
                          {st('Написать докторанту')}
                        </button>
                        <button
                          className="advisor-monitor-mini"
                          onClick={() =>
                            navigate(
                              `${routes.advisor}/meetings?student_id=${candidate.student_user_id}`,
                            )
                          }
                        >
                          {st('Назначить встречу')}
                        </button>
                      </div>
                      <nav className="advisor-monitor-tabs">
                        {Object.entries(workflowTitles).map(([key, title]) => (
                          <button
                            key={key}
                            className={section === key ? 'active' : ''}
                            onClick={() => update({ section: key })}
                          >
                            {st(title)}
                          </button>
                        ))}
                      </nav>
                      <div
                        className="dp120-root"
                        style={{
                          position: 'static',
                          inset: 'auto',
                          zIndex: 'auto',
                          overflow: 'visible',
                        }}
                      >
                        <DoctoralWorkflowForm
                          key={`${candidate.student_user_id}-${section}`}
                          module={section in workflowTitles ? section : 'profile'}
                          studentId={candidate.student_user_id}
                          onChanged={() => setRefresh((x) => x + 1)}
                        />
                      </div>
                    </>
                  ) : (
                    <p>{st('Учётная запись не связана. Требуется настройка назначения.')}</p>
                  )}
                </div>
              </section>
            )
          ) : (
            <>
              <nav className="advisor-monitor-tabs">
                {tabs.map((key, i) => (
                  <button
                    key={key}
                    className={tab === key ? 'active' : ''}
                    onClick={() => update({ tab: key })}
                  >
                    {st(tabNames[i])}
                  </button>
                ))}
              </nav>
              {tab === 'templates' ? (
                <AdvisorResources />
              ) : (
                <>
                  <div className="advisor-monitor-kpis">
                    {[
                      ['total', 'Всего'],
                      ['needs_review', 'На проверке'],
                      ['incomplete', 'Неполные комплекты'],
                      ['deadlines', 'Ближайшие сроки'],
                    ].map(([key, label]) => (
                      <div
                        className="advisor-monitor-kpi"
                        key={key}
                        style={{ '--a': '#49ddb7' } as CSSProperties}
                      >
                        <small>{st(label)}</small>
                        <b>{summary[key] ?? 0}</b>
                      </div>
                    ))}
                  </div>
                  <section className="advisor-monitor-panel">
                    <div className="advisor-monitor-body">
                      <div className="advisor-monitor-filters">
                        {(['program', 'group_name'] as const).map((key) => (
                          <label className="advisor-monitor-field" key={key}>
                            <span>{st(key === 'program' ? 'Программа' : 'Группа')}</span>
                            <select
                              value={key === 'program' ? program : group}
                              onChange={(e) =>
                                update({
                                  [key === 'program' ? 'program' : 'group']: e.target.value,
                                })
                              }
                            >
                              <option value="">{st('Все')}</option>
                              {options(key).map((value) => (
                                <option key={value}>{value}</option>
                              ))}
                            </select>
                          </label>
                        ))}
                        <label className="advisor-monitor-field">
                          <span>{st('Поиск')}</span>
                          <input
                            value={search}
                            onChange={(e) => update({ search: e.target.value })}
                          />
                        </label>
                      </div>
                      <div className="advisor-monitor-student-grid">
                        {visible.map((c) => (
                          <article className="advisor-monitor-card" key={c.id}>
                            <button
                              className="advisor-monitor-mini"
                              onClick={() => update({ candidate_id: c.id })}
                            >
                              {c.full_name || c.academia_id}
                            </button>
                            <p>
                              {c.academia_id} · {year(c.year)} · {c.program || '—'} ·{' '}
                              {c.group_name || '—'}
                            </p>
                            {!c.student_user_id && (
                              <p>
                                {st('Учётная запись не связана. Требуется настройка назначения.')}
                              </p>
                            )}
                            <p>
                              {st('Готовность')}:{' '}
                              {c.progress === null || c.progress === undefined
                                ? '—'
                                : `${c.progress}%`}{' '}
                              · {st(c.review_state === 'NEEDS_REVIEW' ? 'На проверке' : 'В работе')}
                            </p>
                            <p>
                              {st('Неполные разделы')}: {c.incomplete_sections}
                            </p>
                            {c.deadlines?.slice(0, 3).map((d) => (
                              <p key={d.id}>
                                {d.title}:{' '}
                                {new Date(d.date).toLocaleDateString(
                                  locale === 'kz' ? 'kk-KZ' : locale === 'en' ? 'en-GB' : 'ru-RU',
                                )}
                              </p>
                            ))}
                          </article>
                        ))}
                      </div>
                      {!visible.length && <p>{st('Нет докторантов в этой выборке')}</p>}
                    </div>
                  </section>
                </>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
